/**
 * Grail product page.
 *
 *   <product-info>             variant changes via the Section Rendering API (product
 *                              page and Featured product section; combined listings
 *                              swap in the sibling product)
 *   <product-gallery>          stage, thumbnails, swipe, slab/photos toggle, zoom lightbox, 3D
 *   <quantity-input>           +/- honouring the variant's quantity rule
 *   <gift-card-recipient>      recipient fields enabled only when ticked
 *   <product-recommendations>  lazy-loaded related / complementary products
 *
 * Add to cart itself is handled by cart.js for every <form data-cart-form>.
 */
(() => {
  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const formatNumbers = (root) => {
    const format = new Intl.NumberFormat(document.documentElement.lang || undefined);
    root.querySelectorAll('[data-format-number]').forEach((element) => {
      const number = Number(element.getAttribute('value'));
      if (Number.isFinite(number)) element.textContent = format.format(number);
    });
  };

  /* ------------------------------------------------------------------ */
  /* Product info: variant selection                                     */
  /* ------------------------------------------------------------------ */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.state = this.readState(this);
      this.controller = null;
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      formatNumbers(this);
      this.syncSellingPlan();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    readState(root) {
      try {
        return JSON.parse(root.querySelector('[data-product-state]')?.textContent || '{}');
      } catch {
        return {};
      }
    }

    onChange = (event) => {
      const target = event.target;

      if (target.matches('input[name^="purchase-"]')) {
        this.syncSellingPlan();
        return;
      }

      const group = target.closest('[data-option-position]');
      if (!group) return;

      const chosen = target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      if (!chosen?.dataset.optionValueId) return;

      const selected = group.querySelector('[data-option-selected]');
      if (selected) selected.textContent = chosen.value;

      const ids = this.selectedOptionValueIds();
      ids[Number(group.dataset.optionPosition) - 1] = chosen.dataset.optionValueId;

      // Combined listings: this value belongs to a sibling product, so render that one instead.
      // A Featured product section always renders its own product setting, so it opens the page.
      const productUrl = chosen.dataset.productUrl;
      if (productUrl && productUrl !== this.dataset.productUrl) {
        if (this.dataset.updateUrl === 'false') window.location.assign(productUrl);
        else this.renderSelection(ids.filter(Boolean), productUrl);
        return;
      }

      this.renderSelection(ids.filter(Boolean));
    };

    onClick = (event) => {
      const opener = event.target.closest('[data-dialog-open]');
      if (opener) {
        const dialog = document.getElementById(opener.dataset.dialogOpen);
        if (dialog && !dialog.open) {
          dialog.showModal();
          const key = CSS.escape(opener.dataset.dialogOpen);
          dialog.addEventListener('close', () => this.querySelector(`[data-dialog-open="${key}"]`)?.focus(), { once: true });
        }
        return;
      }

      const closer = event.target.closest('[data-dialog-close]');
      if (closer) {
        closer.closest('dialog')?.close();
        return;
      }

      // Click on a side-sheet dialog's backdrop closes it.
      if (event.target.matches('dialog.pickup-dialog')) {
        const rect = event.target.getBoundingClientRect();
        const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!inside) event.target.close();
      }
    };

    selectedOptionValueIds() {
      const ids = [...(this.state.optionValueIds || [])];
      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const position = Number(group.dataset.optionPosition) - 1;
        const chosen = group.tagName === 'SELECT' ? group.selectedOptions[0] : group.querySelector('input:checked');
        if (chosen?.dataset.optionValueId) ids[position] = chosen.dataset.optionValueId;
      });
      return ids;
    }

    /** Fetches this section for the chosen option values; a productUrl other than ours swaps in that product. */
    async renderSelection(optionValueIds, productUrl = this.dataset.productUrl) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setAttribute('aria-busy', 'true');
      this.setSubmitting(true);

      const url = `${productUrl}?section_id=${encodeURIComponent(this.dataset.sectionId)}&option_values=${optionValueIds.join(',')}`;

      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
        const text = await response.text();
        if (controller.signal.aborted) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        const next = html.querySelector('product-info');
        if (!next) throw new Error('Section markup missing');
        if (next.dataset.productUrl !== this.dataset.productUrl) this.swapProduct(next);
        else this.apply(next);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        // The form still holds the previous variant: put the picker back to match it.
        this.revertSelection();
        this.setSubmitting(false);
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.removeAttribute('aria-busy');
        }
      }
    }

    /** Re-checks the option values of the variant currently in the form (this.state). */
    revertSelection() {
      const ids = this.state.optionValueIds || [];
      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const id = ids[Number(group.dataset.optionPosition) - 1];
        if (!id) return;
        const match = group.querySelector(`[data-option-value-id="${CSS.escape(String(id))}"]`);
        if (!match) return;
        if (match.tagName === 'OPTION') match.selected = true;
        else match.checked = true;
        const selected = group.querySelector('[data-option-selected]');
        if (selected) selected.textContent = match.value;
      });
    }

    /** Disable add to cart while a selection is loading, so the old variant can't be added. */
    setSubmitting(loading) {
      this.querySelectorAll('form[data-cart-form] [type="submit"]').forEach((button) => {
        if (loading) {
          button.disabled = true;
        } else {
          button.disabled = !(this.state.exists && this.state.available);
        }
      });
    }

    apply(next) {
      const activeId = document.activeElement?.id;

      this.querySelectorAll('[data-product-update]').forEach((current) => {
        const key = current.dataset.productUpdate;
        const incoming = next.querySelector(`[data-product-update="${CSS.escape(key)}"]`);
        if (!incoming) return;
        const preserved = current.hasAttribute('data-preserve-inputs') ? this.captureChecked(current) : null;
        if (current.innerHTML !== incoming.innerHTML) current.innerHTML = incoming.innerHTML;
        current.hidden = incoming.hidden;
        if (preserved) this.restoreChecked(current, preserved);
      });

      const stateScript = this.querySelector('[data-product-state]');
      const nextState = next.querySelector('[data-product-state]');
      if (stateScript && nextState) stateScript.textContent = nextState.textContent;
      this.state = this.readState(next);

      this.updateForm(next);
      this.updateUrl();
      this.syncSellingPlan();
      formatNumbers(this);

      if (this.state.mediaId) this.querySelector('product-gallery')?.showMedia(this.state.mediaId);

      if (activeId) {
        const focusTarget = document.getElementById(activeId);
        if (focusTarget && focusTarget !== document.activeElement) focusTarget.focus({ preventScroll: true });
      }

      this.dispatchEvent(
        new CustomEvent('grail:variant-change', {
          bubbles: true,
          detail: { sectionId: this.dataset.sectionId, variantId: this.state.variantId, available: this.state.available },
        })
      );
    }

    updateForm(next) {
      const { exists, available, variantId } = this.state;

      this.querySelectorAll('[data-product-variant-id]').forEach((input) => {
        if (variantId) input.value = variantId;
        input.disabled = !exists;
      });

      this.querySelectorAll('[data-buy-now]').forEach((container) => {
        container.hidden = !(exists && available);
      });

      this.querySelectorAll('[data-quantity-input]').forEach((input) => {
        const incoming = input.id && next.querySelector(`#${CSS.escape(input.id)}`);
        if (!incoming) return;
        input.closest('quantity-input')?.applyRules(incoming.min, incoming.max, incoming.step);
      });
    }

    /**
     * Combined listings: the chosen value is another product. Its media, options and form
     * all differ, so the whole element is replaced (the new one upgrades on insertion).
     */
    swapProduct(next) {
      const activeId = document.activeElement?.id;
      const incoming = document.importNode(next, true);
      this.replaceWith(incoming);
      incoming.updateUrl?.(true);
      window.Shopify?.PaymentButton?.init?.();

      if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });

      incoming.dispatchEvent(
        new CustomEvent('grail:variant-change', {
          bubbles: true,
          detail: { sectionId: incoming.dataset.sectionId, variantId: incoming.state?.variantId, available: incoming.state?.available },
        })
      );
    }

    /** Product page only: the Featured product section sets data-update-url="false". */
    updateUrl(swapped = false) {
      if (this.dataset.updateUrl === 'false') return;
      if (!this.state.exists || !this.state.variantId) return;
      const url = new URL(window.location.href);
      // After a combined-listing swap the address moves to the sibling product
      if (swapped) url.pathname = new URL(this.dataset.productUrl, url.origin).pathname;
      url.searchParams.set('variant', this.state.variantId);
      window.history.replaceState(window.history.state, '', url);
    }

    syncSellingPlan() {
      this.querySelectorAll('[data-selling-plan]').forEach((hidden) => {
        const container = hidden.parentElement;
        const checked = container.querySelector('input[name^="purchase-"]:checked');
        hidden.value = checked ? checked.value : '';
        hidden.disabled = hidden.value === '';
      });
    }

    captureChecked(root) {
      const checked = new Map();
      root.querySelectorAll('input[type="radio"]:checked').forEach((input) => checked.set(input.name, input.value));
      return checked;
    }

    restoreChecked(root, checked) {
      checked.forEach((value, name) => {
        const match = [...root.querySelectorAll('input[type="radio"]')].find((input) => input.name === name && input.value === value);
        if (match && !match.disabled) match.checked = true;
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Gallery                                                             */
  /* ------------------------------------------------------------------ */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.gallery__slide[data-media-id]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.stage = this.querySelector('[data-gallery-stage]');
      this.lightbox = this.querySelector('[data-lightbox]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.lightboxIndex = 0;

      this.addEventListener('click', this.onClick);
      this.stage?.addEventListener('pointerdown', this.onPointerDown);
      this.stage?.addEventListener('pointermove', this.onSheenMove);
      this.stage?.addEventListener('keydown', this.onStageKeydown);

      if (this.lightbox) {
        this.lightbox.addEventListener('keydown', this.onLightboxKeydown);
        this.lightbox.addEventListener('pointermove', this.onZoomMove);
        this.lightbox.addEventListener('close', this.onLightboxClose);
      }

      this.updateIndicators(false);
      this.setupModels();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    get isRtl() {
      return getComputedStyle(this).direction === 'rtl';
    }

    onClick = (event) => {
      const view = event.target.closest('[data-gallery-view]');
      if (view) return this.setView(view.dataset.galleryView);

      const step = event.target.closest('[data-gallery-step]');
      if (step) return this.go(this.index + Number(step.dataset.galleryStep));

      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) {
        this.setView('photos');
        return this.showMedia(thumb.dataset.galleryThumb);
      }

      const opener = event.target.closest('[data-lightbox-open]');
      if (opener) {
        if (this.suppressClick) return;
        return this.openLightbox(opener.dataset.lightboxOpen);
      }

      if (!this.lightbox?.contains(event.target)) return;

      const lightboxStep = event.target.closest('[data-lightbox-step]');
      if (lightboxStep) return this.showLightboxItem(this.lightboxIndex + Number(lightboxStep.dataset.lightboxStep));

      if (event.target.closest('[data-lightbox-close]')) return this.lightbox.close();
      if (event.target.closest('[data-lightbox-zoom]')) return this.toggleZoom();
      if (event.target.closest('.lightbox__image')) return this.toggleZoom(event);
    };

    setView(view) {
      if (this.dataset.view === view) return;
      this.dataset.view = view;
      this.querySelectorAll('[data-gallery-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.galleryPanel !== view;
      });
      this.querySelectorAll('[data-gallery-view]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.galleryView === view));
      });
      if (view !== 'photos') this.pauseMedia(this.slides[this.index]);
      this.updateIndicators(false);
    }

    go(index) {
      const count = this.slides.length;
      if (count < 2) return;
      this.showSlide((index + count) % count);
    }

    showMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0 && index !== this.index) this.showSlide(index);
    }

    showSlide(index) {
      const previous = this.slides[this.index];
      const next = this.slides[index];
      if (!next) return;
      if (previous && previous !== next) {
        previous.classList.remove('is-active');
        previous.inert = true;
        this.pauseMedia(previous);
      }
      next.classList.add('is-active');
      next.inert = false;
      this.index = index;
      this.updateIndicators(true);
    }

    updateIndicators(scrollThumb) {
      const active = this.slides[this.index];
      const counter = this.querySelector('[data-gallery-index]');
      if (counter) counter.textContent = String(this.index + 1);

      this.thumbs.forEach((thumb) => {
        const current = this.dataset.view === 'photos' && thumb.dataset.galleryThumb === active?.dataset.mediaId;
        if (current) {
          thumb.setAttribute('aria-current', 'true');
          if (scrollThumb) {
            const list = thumb.closest('.gallery__thumbs');
            const left = thumb.offsetLeft - list.offsetLeft - (list.clientWidth - thumb.offsetWidth) / 2;
            list.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    pauseMedia(slide) {
      if (!slide) return;
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('iframe').forEach((iframe) => {
        const src = iframe.src || '';
        if (src.includes('youtube')) {
          iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
        } else if (src.includes('vimeo')) {
          iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
        }
      });
      slide.querySelectorAll('model-viewer').forEach((model) => model.modelViewerUI?.pause?.());
    }

    onStageKeydown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target.closest('model-viewer, video')) return;
      event.preventDefault();
      const forward = (event.key === 'ArrowRight') !== this.isRtl;
      this.go(this.index + (forward ? 1 : -1));
      this.slides[this.index]?.querySelector('[data-lightbox-open]')?.focus({ preventScroll: true });
    };

    onPointerDown = (event) => {
      if (event.pointerType === 'mouse' || this.slides.length < 2) return;
      if (event.target.closest('model-viewer, video, iframe')) return;
      const startX = event.clientX;
      const startY = event.clientY;

      const finish = (end) => {
        window.removeEventListener('pointerup', finish);
        window.removeEventListener('pointercancel', finish);
        if (end.type === 'pointercancel') return;
        const dx = end.clientX - startX;
        const dy = end.clientY - startY;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        this.suppressClick = true;
        setTimeout(() => (this.suppressClick = false), 350);
        const forward = dx < 0 !== this.isRtl;
        this.go(this.index + (forward ? 1 : -1));
      };

      window.addEventListener('pointerup', finish);
      window.addEventListener('pointercancel', finish);
    };

    onSheenMove = (event) => {
      if (reduceMotion.matches || event.pointerType !== 'mouse') return;
      const rect = this.stage.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      this.slides[this.index]?.style.setProperty('--sx', `${x.toFixed(1)}%`);
    };

    /* Lightbox */
    openLightbox(mediaId) {
      if (!this.lightbox) return;
      this.lightboxItems = [...this.lightbox.querySelectorAll('.lightbox__item')];
      const index = Math.max(0, this.lightboxItems.findIndex((item) => item.dataset.mediaId === String(mediaId)));
      this.showLightboxItem(index);
      this.lightbox.showModal();
    }

    showLightboxItem(index) {
      const items = this.lightboxItems || [];
      if (!items.length) return;
      this.resetZoom();
      this.lightboxIndex = (index + items.length) % items.length;
      items.forEach((item, i) => {
        item.hidden = i !== this.lightboxIndex;
        const near = Math.abs(i - this.lightboxIndex) <= 1 || (this.lightboxIndex === 0 && i === items.length - 1);
        if (near) item.querySelector('img')?.setAttribute('loading', 'eager');
      });
      const counter = this.lightbox.querySelector('[data-lightbox-index]');
      if (counter) counter.textContent = String(this.lightboxIndex + 1);
    }

    toggleZoom(event) {
      const item = this.lightboxItems?.[this.lightboxIndex];
      if (!item) return;
      const zoomed = !item.classList.contains('is-zoomed');
      if (event && zoomed) this.setZoomOrigin(item, event);
      item.classList.toggle('is-zoomed', zoomed);
      this.lightbox.querySelector('[data-lightbox-zoom]')?.setAttribute('aria-pressed', String(zoomed));
    }

    setZoomOrigin(item, event) {
      const image = item.querySelector('img');
      if (!image) return;
      const rect = image.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
      image.style.setProperty('--zx', `${x}%`);
      image.style.setProperty('--zy', `${y}%`);
    }

    resetZoom() {
      this.lightboxItems?.forEach((item) => item.classList.remove('is-zoomed'));
      this.lightbox?.querySelector('[data-lightbox-zoom]')?.setAttribute('aria-pressed', 'false');
    }

    onZoomMove = (event) => {
      const item = this.lightboxItems?.[this.lightboxIndex];
      if (!item?.classList.contains('is-zoomed') || event.pointerType !== 'mouse') return;
      this.setZoomOrigin(item, event);
    };

    onLightboxKeydown = (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const forward = (event.key === 'ArrowRight') !== this.isRtl;
        this.showLightboxItem(this.lightboxIndex + (forward ? 1 : -1));
      }
    };

    onLightboxClose = () => {
      const item = this.lightboxItems?.[this.lightboxIndex];
      this.resetZoom();
      if (!item) return;
      this.setView('photos');
      this.showMedia(item.dataset.mediaId);
      this.slides[this.index]?.querySelector('[data-lightbox-open]')?.focus({ preventScroll: true });
    };

    /* 3D models + AR */
    setupModels() {
      const json = this.querySelector('[data-gallery-models]');
      if (!json) return;
      const load = () => {
        if (!window.Shopify?.loadFeatures) return;
        window.Shopify.loadFeatures([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: (error) => {
              if (error) return;
              this.querySelectorAll('model-viewer').forEach((model) => {
                if (!model.modelViewerUI) model.modelViewerUI = new window.Shopify.ModelViewerUI(model);
              });
            },
          },
          {
            name: 'shopify-xr',
            version: '1.0',
            onLoad: (error) => {
              if (error || !window.ShopifyXR) return;
              try {
                window.ShopifyXR.addModels(JSON.parse(json.textContent));
                window.ShopifyXR.setupXRElements();
              } catch (e) {
                console.error(e);
              }
            },
          },
        ]);
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', load, { once: true });
      } else {
        load();
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Quantity                                                            */
  /* ------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.addEventListener('click', this.onClick);
      this.input.addEventListener('change', this.onInputChange);
      this.updateButtons();
    }

    get rules() {
      const min = Number(this.input.min) || 1;
      const max = this.input.max === '' ? Infinity : Number(this.input.max);
      const step = Number(this.input.step) || 1;
      return { min, max, step };
    }

    normalize(value) {
      const { min, max, step } = this.rules;
      let next = Number.isFinite(value) ? value : min;
      next = Math.round((next - min) / step) * step + min;
      if (next > max) next = max - ((max - min) % step);
      return Math.max(min, next);
    }

    onClick = (event) => {
      const button = event.target.closest('[data-quantity-step]');
      if (!button) return;
      const value = this.normalize(Number(this.input.value) + Number(button.dataset.quantityStep) * this.rules.step);
      if (String(value) === this.input.value) return;
      this.input.value = value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    onInputChange = () => {
      const value = this.normalize(Number(this.input.value));
      if (String(value) !== this.input.value) this.input.value = value;
      this.updateButtons();
    };

    applyRules(min, max, step) {
      this.input.min = min || 1;
      if (max) this.input.max = max;
      else this.input.removeAttribute('max');
      this.input.step = step || 1;
      this.input.value = this.normalize(Number(this.input.value));
      this.updateButtons();
    }

    updateButtons() {
      const { min, max, step } = this.rules;
      const value = Number(this.input.value);
      const minus = this.querySelector('[data-quantity-step="-1"]');
      const plus = this.querySelector('[data-quantity-step="1"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value + step > max;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Gift card recipient                                                 */
  /* ------------------------------------------------------------------ */
  class GiftCardRecipient extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-recipient-toggle]');
      this.fields = this.querySelector('[data-recipient-fields]');
      if (!this.toggle || !this.fields) return;
      this.toggle.addEventListener('change', this.sync);
      window.addEventListener('pageshow', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.sync);
    }

    sync = () => {
      const on = this.toggle.checked;
      this.fields.hidden = !on;
      this.fields.querySelectorAll('input, textarea').forEach((field) => {
        field.disabled = !on;
      });
      const offset = this.querySelector('[data-recipient-offset]');
      if (offset) offset.value = String(new Date().getTimezoneOffset());
    };
  }

  /* ------------------------------------------------------------------ */
  /* Recommendations                                                     */
  /* ------------------------------------------------------------------ */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-loaded') || !this.dataset.url) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      // Leaving the page mid-load cancels the request; abort it ourselves so
      // that's a quiet AbortError instead of a logged "Failed to fetch"
      const controller = new AbortController();
      const abort = () => controller.abort();
      window.addEventListener('pagehide', abort, { once: true });
      try {
        const response = await fetch(this.dataset.url, { signal: controller.signal });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = html.querySelector('product-recommendations');
        if (!next?.hasAttribute('data-loaded')) return;
        this.innerHTML = next.innerHTML;
        this.setAttribute('data-loaded', '');
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        window.removeEventListener('pagehide', abort);
      }
    }
  }

  define('product-info', ProductInfo);
  define('product-gallery', ProductGallery);
  define('quantity-input', QuantityInput);
  define('gift-card-recipient', GiftCardRecipient);
  define('product-recommendations', ProductRecommendations);
})();
